import { type DragEvent, type FormEvent, type JSX, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import {
  Badge,
  Button,
  Card,
  ConfirmModal,
  Input,
  Skeleton,
  Spinner,
} from '@/components/ui';
import {
  useAddDNC,
  useDNCList,
  useImportDNC,
  useInboundList,
  useRemoveDNC,
  type DNCImportResult,
  type DNCItem,
  type InboundItem,
} from '@/hooks/useInbound';
import { ApiError } from '@/lib/api';
import { formatRelativeTime, truncate } from '@/lib/format';
import { isValidE164 } from '@/lib/sms';
import { cn } from '@/lib/utils';

type Tab = 'inbound' | 'dnc';

export default function InboundPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const visible = useTabVisibility();

  const [tab, setTab] = useState<Tab>('inbound');

  const inbound = useInboundList({ enabled: tab === 'inbound' && visible });
  const dnc = useDNCList({ enabled: tab === 'dnc' && visible });

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <header className="mb-6">
        <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-tight m-0">
          {t('nav_inbound')}
        </h1>
        <p className="text-[13px] text-text-3 mt-1">{t('inbound_sub')}</p>
      </header>

      {/* Tab switcher */}
      <div className="flex gap-1 mb-4 border-b border-line">
        <TabButton active={tab === 'inbound'} onClick={() => setTab('inbound')}>
          {t('inbound_tab_messages', { total: (inbound.data?.total ?? 0).toLocaleString(lang) })}
        </TabButton>
        <TabButton active={tab === 'dnc'} onClick={() => setTab('dnc')}>
          {t('inbound_tab_dnc', { total: (dnc.data?.total ?? 0).toLocaleString(lang) })}
        </TabButton>
      </div>

      {tab === 'inbound' ? (
        <InboundTable
          loading={inbound.isLoading}
          isError={inbound.isError}
          items={inbound.data?.items ?? []}
          lang={lang}
          t={t}
          onRetry={() => void inbound.refetch()}
        />
      ) : (
        <DNCTab
          loading={dnc.isLoading}
          isError={dnc.isError}
          items={dnc.data?.items ?? []}
          lang={lang}
          t={t}
          onRetry={() => void dnc.refetch()}
        />
      )}
    </div>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function TabButton({ active, onClick, children }: TabButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-4 py-2 text-[13px] font-medium border-b-2 -mb-px transition-colors',
        active
          ? 'border-volt text-text'
          : 'border-transparent text-text-3 hover:text-text-2',
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Inbound table
// ---------------------------------------------------------------------------
interface InboundTableProps {
  loading: boolean;
  isError: boolean;
  items: InboundItem[];
  lang: string;
  t: (k: string, opts?: Record<string, unknown>) => string;
  onRetry: () => void;
}

function InboundTable({ loading, isError, items, lang, t, onRetry }: InboundTableProps): JSX.Element {
  return (
    <Card padding="none">
      {loading ? (
        <div className="p-5 space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : isError ? (
        <div className="p-8 text-center">
          <p className="text-[13px] text-danger">{t('inbound_load_error')}</p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
            {t('inbound_retry')}
          </Button>
        </div>
      ) : items.length === 0 ? (
        <div className="p-12 text-center">
          <p className="text-[14px] text-text-2 font-medium">{t('inbound_empty_title')}</p>
          <p className="text-[13px] text-text-3 mt-1">{t('inbound_empty_sub')}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                <th className="px-5 py-2.5 font-medium">{t('inbound_col_when')}</th>
                <th className="px-5 py-2.5 font-medium">{t('inbound_col_from')}</th>
                <th className="px-5 py-2.5 font-medium">{t('inbound_col_to')}</th>
                <th className="px-5 py-2.5 font-medium">{t('inbound_col_body')}</th>
                <th className="px-5 py-2.5 font-medium">{t('inbound_col_keyword')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id} className="border-b border-line last:border-0 hover:bg-surface-2">
                  <td className="px-5 py-3 text-text-3 whitespace-nowrap">
                    <span title={new Date(m.received_at).toLocaleString(lang)}>
                      {formatRelativeTime(m.received_at, lang)}
                    </span>
                  </td>
                  <td className="px-5 py-3 num text-text whitespace-nowrap">{m.from_phone}</td>
                  <td className="px-5 py-3 num text-text-2 whitespace-nowrap">{m.to_phone}</td>
                  <td className="px-5 py-3 text-text-2 max-w-[420px]">
                    <span title={m.body}>{truncate(m.body, 100)}</span>
                  </td>
                  <td className="px-5 py-3">
                    {m.is_keyword && m.keyword ? (
                      <Badge variant={keywordVariant(m.keyword)}>{m.keyword}</Badge>
                    ) : (
                      <span className="text-[12px] text-text-4">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function keywordVariant(keyword: string): 'danger' | 'success' | 'info' | 'gray' {
  switch (keyword) {
    case 'STOP':
    case 'STOPALL':
    case 'UNSUBSCRIBE':
    case 'CANCEL':
    case 'END':
    case 'QUIT':
      return 'danger';
    case 'START':
    case 'UNSTOP':
    case 'YES':
      return 'success';
    case 'HELP':
    case 'INFO':
      return 'info';
    default:
      return 'gray';
  }
}

// ---------------------------------------------------------------------------
// DNC tab — list + add + remove
// ---------------------------------------------------------------------------
interface DNCTabProps {
  loading: boolean;
  isError: boolean;
  items: DNCItem[];
  lang: string;
  t: (k: string, opts?: Record<string, unknown>) => string;
  onRetry: () => void;
}

function DNCTab({ loading, isError, items, lang, t, onRetry }: DNCTabProps): JSX.Element {
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<DNCItem | null>(null);

  const add = useAddDNC();
  const remove = useRemoveDNC();

  const submitAdd = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!isValidE164(phone)) {
      setError(t('inbound_dnc_err_e164'));
      return;
    }
    try {
      await add.mutateAsync({ phone_e164: phone.trim(), reason: 'manual' });
      setPhone('');
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError(err instanceof Error ? err.message : t('inbound_err_generic'));
    }
  };

  const confirmRemove = async () => {
    if (!pendingRemove) return;
    try {
      await remove.mutateAsync(pendingRemove.phone_e164);
    } finally {
      setPendingRemove(null);
    }
  };

  return (
    <div>
      <Card padding="lg" className="mb-4">
        <h2 className="display text-[16px] font-semibold text-text mb-3">
          {t('inbound_dnc_add_title')}
        </h2>
        {error && (
          <div className="px-3 py-2 mb-3 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
            {error}
          </div>
        )}
        <form onSubmit={submitAdd} className="flex gap-3 items-end">
          <div className="flex-1">
            <label htmlFor="dnc-phone" className="block text-[12px] font-medium text-text-2 mb-1.5">
              {t('inbound_dnc_field_phone')}
            </label>
            <Input
              id="dnc-phone"
              placeholder="+15551234567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={add.isPending}
            />
          </div>
          <Button type="submit" disabled={add.isPending}>
            {add.isPending ? (
              <>
                <Spinner />
                <span>{t('inbound_dnc_adding')}</span>
              </>
            ) : (
              <>
                <Icon.warn width="14" height="14" />
                <span>{t('inbound_dnc_add')}</span>
              </>
            )}
          </Button>
        </form>
        <p className="text-[12px] text-text-4 mt-2">{t('inbound_dnc_add_help')}</p>
      </Card>

      <DNCImportCard t={t} lang={lang} />

      <Card padding="none">
        {loading ? (
          <div className="p-5 space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : isError ? (
          <div className="p-8 text-center">
            <p className="text-[13px] text-danger">{t('inbound_load_error')}</p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
              {t('inbound_retry')}
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-[14px] text-text-2 font-medium">{t('inbound_dnc_empty_title')}</p>
            <p className="text-[13px] text-text-3 mt-1">{t('inbound_dnc_empty_sub')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium">{t('inbound_dnc_col_phone')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('inbound_dnc_col_reason')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('inbound_dnc_col_source')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('inbound_dnc_col_added')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('inbound_dnc_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d.id} className="border-b border-line last:border-0 hover:bg-surface-2">
                    <td className="px-5 py-3 num text-text whitespace-nowrap">{d.phone_e164}</td>
                    <td className="px-5 py-3 text-text-2">{d.reason ?? '—'}</td>
                    <td className="px-5 py-3 text-text-2">{d.source ?? '—'}</td>
                    <td className="px-5 py-3 text-text-3 whitespace-nowrap">
                      <span title={new Date(d.added_at).toLocaleString(lang)}>
                        {formatRelativeTime(d.added_at, lang)}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setPendingRemove(d)}
                        disabled={remove.isPending}
                      >
                        {t('inbound_dnc_remove')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmModal
        open={pendingRemove !== null}
        title={t('inbound_dnc_remove')}
        description={t('inbound_dnc_confirm_remove', { phone: pendingRemove?.phone_e164 ?? '' })}
        confirmLabel={t('inbound_dnc_remove')}
        cancelLabel={t('inbound_dnc_cancel')}
        tone="danger"
        busy={remove.isPending}
        onConfirm={() => void confirmRemove()}
        onClose={() => setPendingRemove(null)}
      />
    </div>
  );
}

function useTabVisibility(): boolean {
  const [visible, setVisible] = useState<boolean>(() =>
    typeof document === 'undefined' ? true : document.visibilityState === 'visible',
  );
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onChange = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

// ---------------------------------------------------------------------------
// DNC bulk import — drop a CSV/XLSX, phones get added in one request. No
// column picker on purpose: the server auto-detects the phone column (and
// handles headerless one-number-per-line files), so the whole flow is
// "drop file → see counts". Same dropzone shape as the contact import.
// ---------------------------------------------------------------------------
interface DNCImportCardProps {
  t: (k: string, opts?: Record<string, unknown>) => string;
  lang: string;
}

function DNCImportCard({ t, lang }: DNCImportCardProps): JSX.Element {
  const importDNC = useImportDNC();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState<DNCImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onPickFile = async (f: File) => {
    setError(null);
    setResult(null);
    try {
      setResult(await importDNC.mutateAsync(f));
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError(err instanceof Error ? err.message : t('inbound_dnc_import_err'));
    } finally {
      // Reset so picking the same file again re-fires onChange.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) void onPickFile(f);
  };

  const busy = importDNC.isPending;
  const n = (v: number) => v.toLocaleString(lang);

  return (
    <Card padding="lg" className="mb-4">
      <h2 className="display text-[16px] font-semibold text-text mb-1">
        {t('inbound_dnc_import_title')}
      </h2>
      <p className="text-[12px] text-text-4 mb-3">{t('inbound_dnc_import_help')}</p>
      {error && (
        <div className="px-3 py-2 mb-3 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
          {error}
        </div>
      )}
      {result && !error && (
        <div className="px-3 py-2 mb-3 bg-success-bg border border-success-bd rounded-md text-[13px] text-success">
          {t('inbound_dnc_import_result', {
            inserted: n(result.inserted),
            skipped: n(result.skipped),
            invalid: n(result.invalid),
            total: n(result.total_rows),
          })}
        </div>
      )}
      <div
        onClick={() => !busy && fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={busy ? undefined : onDrop}
        role="button"
        tabIndex={0}
        aria-disabled={busy}
        onKeyDown={(e) => {
          if (!busy && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        className={cn(
          'rounded-md border-2 border-dashed py-8 px-6 text-center transition-colors',
          busy ? 'cursor-wait opacity-70' : 'cursor-pointer',
          dragOver ? 'border-volt bg-volt-50' : 'border-line-strong bg-surface-2 hover:bg-surface',
        )}
      >
        <div className="text-[14px] text-text font-medium mb-1 inline-flex items-center gap-2">
          {busy && <Spinner />}
          <span>{busy ? t('inbound_dnc_importing') : t('inbound_dnc_import_drop')}</span>
        </div>
        <div className="text-[12px] text-text-3">{t('inbound_dnc_import_accepts')}</div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls,.xlsm"
          className="hidden"
          disabled={busy}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onPickFile(f);
          }}
        />
      </div>
    </Card>
  );
}
